import { useEffect, useState, useRef } from 'react';
import { DateTime } from "luxon";
import AjaxUtil from '../../common/js/AjaxUtil.ts';

const useDebouncedValue = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(handler)
  }, [value, delay])

  return debouncedValue
}

export default function DriverLogsListViewModel() {

  const moment = DateTime.now().startOf('hour');
  const start = moment.startOf('hour')
    .toFormat('yyyy-MM-dd HH:mm')
    .replace(' ', 'T')
  const end = moment.plus({ hours: 1, })
    .toFormat('yyyy-MM-dd HH:mm')
    .replace(' ', 'T')

  const [deviceId, setDeviceId] = useState('');
  const [driverNo, setDriverNo] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [startDate, setStartDate] = useState(start);
  const [endDate, setEndDate] = useState(end);
  const [logs, setLogs] = useState([]);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const [includeGps, setIncludeGps] = useState(true);
  const [includeMessaging, setIncludeMessaging] = useState(true);
  const [includeJobEvents, setIncludeJobEvents] = useState(true);
  const [includeLogin, setIncludeLogin] = useState(true);
  const [includeOther, setIncludeOther] = useState(true);
  const [mapMarker, setMapMarker] = useState();
  const [gpsTrail, setGpsTrail] = useState([]);
  const [apiKey, setApiKey] = useState(''); // Google maps API key
  const [searchArgs, setSearchArgs] = useState({});
  const [xmlSearch, setXmlSearch] = useState('')
  const debouncedXmlSearch = useDebouncedValue(xmlSearch, 250)

  const isSearching = useRef(false);

  const search = () => {
    //{"start":"2024-04-16 01:00:00","end":"2024-04-16 14:59:59","deviceId":"74688"}
    //{"start":"2024-04-16 01:00:00","end":"2024-04-16 14:59:59","deviceId":"","driverNo":"3333","company":"PUMA"}
    const startStamp = startDate.replace('T', ' ')
    const endStamp = endDate.replace('T', ' ')

    doSearch(startStamp, endStamp, deviceId);
  };

  const doSearch = (start, end, dId) => {
    setLogs([]);
    setGpsTrail([]);
    setMapMarker(undefined);
    isSearching.current = true
    setRequestInProgress(true);

    // searchNext(start, end, dId, true);
    setSearchArgs({
      start,
      end,
      dId,
      first: true,
    })
  };

  const clearResults = () => {
    setLogs([]);
    setGpsTrail([]);
    setMapMarker(undefined);
  };

  const searchByDeviceId = (dId) => {
    setDeviceId(dId);
    setDriverNo('');
    setCompanyCode('');

    const startStamp = startDate.replace('T', ' ')
    const endStamp = endDate.replace('T', ' ')

    doSearch(startStamp, endStamp, dId);
  };

  useEffect(() => {
    if (
      'start' in searchArgs &&
      'end' in searchArgs &&
      'dId' in searchArgs &&
      'first' in searchArgs
    ) {
      const { start, end, dId, first } = searchArgs;
      searchNext(start, end, dId, first);
    }
  }, [searchArgs]);

  const searchNext = (start, end, dId, first) => {

    var url = "api/v1/getDriverLogs";

    const args = { start, end, deviceId: dId };
    if (driverNo.length > 0) args.driverNo = driverNo;
    if (companyCode.length > 0) args.company = companyCode;

    const success = (result) => searchCallback(dId, result, first);

    AjaxUtil.fetchPost({ url, args, success });

  }

  const searchCallback = (dId, result, first) => {
    if (!isSearching.current) {
      console.log('Search cancelled');
      return;
    }

    if (result.error) {
      console.error(result.error);
      setRequestInProgress(false);
      return;
    } else if (result.data.length > 0) {
      //console.log("Logs length: "+logs.length);
      const newLogs = result.data.map(r => {
        if (!first) {
          r.index += logs.length;
        }
        return r;
      });
      if (first) {
        //console.log("Setting logs");
        setLogs(newLogs);
      } else {
        //console.log("Appending logs");
        setLogs([
          ...logs,
          ...newLogs
        ]);
      }

      // Add the GPS events to the gps trail.
      const newGpsTrail = newLogs.filter(r => r.coord);
      setGpsTrail(gpsTrail => [...gpsTrail, ...newGpsTrail]);
    }

    if ('finish' in result && result.finish === true) {
      setRequestInProgress(false);
    } else {
      setSearchArgs({
        start: result.start,
        end: searchArgs.end,
        dId,
        first: false
      })
    }
  }

  const cancelSearch = () => {
    setRequestInProgress(false)
    isSearching.current = false;
  };

  return {
    deviceId, setDeviceId,
    driverNo, setDriverNo,
    companyCode, setCompanyCode,
    startDate, setStartDate,
    endDate, setEndDate,
    logs, setLogs,
    requestInProgress, setRequestInProgress,
    includeGps, setIncludeGps,
    includeMessaging, setIncludeMessaging,
    includeJobEvents, setIncludeJobEvents,
    includeLogin, setIncludeLogin,
    includeOther, setIncludeOther,
    mapMarker, setMapMarker,
    gpsTrail, setGpsTrail,
    apiKey, setApiKey,
    search,
    searchByDeviceId,
    cancelSearch,
    clearResults,
    xmlSearch: debouncedXmlSearch,
    setXmlSearch,
  };

}
